import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { forkJoin } from 'rxjs';
import { CrmAccount, CrmClient } from '../../crm/crm.model';
import { CrmService } from '../../crm/crm.service';
import { companyOwnedWarehouses, masterDataMap, warehousingBreadcrumb, warehousingHomeBreadcrumb } from '../warehousing-common';
import { WarehousingItem, WarehousingItemTransactionLedgerPage, WarehousingItemTransactionLedgerRow, WarehousingSearchFilters, WarehousingStockMovement } from '../warehousing.model';
import { WarehousingPageHeaderComponent } from '../warehousing-page-header.component';
import { WarehousingSearchPanelComponent } from '../warehousing-search-panel.component';
import { WarehousingService } from '../warehousing.service';
import { itemSizeLabel } from '../warehousing-item-display';

@Component({
  selector: 'app-warehousing-stock-ledger',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, RouterModule, BreadcrumbModule, ButtonModule, DatePickerModule, IftaLabelModule,
    InputGroupModule, InputGroupAddonModule, InputTextModule, ProgressBarModule, SelectModule, TableModule, TagModule,
    WarehousingPageHeaderComponent, WarehousingSearchPanelComponent
  ],
  templateUrl: './warehousing-stock-ledger.component.html'
})
export class WarehousingStockLedgerComponent implements OnInit {
  readonly itemSizeLabel = itemSizeLabel;
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly warehousingService = inject(WarehousingService);
  private readonly crmService = inject(CrmService);

  isLoading = false;
  isExporting = false;
  referenceDataLoaded = false;
  first = 0;
  pageSize = 25;
  totalRecords = 0;
  rows: WarehousingItemTransactionLedgerRow[] = [];
  ledger?: WarehousingItemTransactionLedgerPage;
  breadcrumbItems: MenuItem[] = warehousingBreadcrumb('Item Transactions Ledger', 'On Hand Stock', '/warehousing/stock');
  homeBreadcrumb: MenuItem = warehousingHomeBreadcrumb();
  masterData = masterDataMap();
  clients: CrmClient[] = [];
  accounts: CrmAccount[] = [];
  items: WarehousingItem[] = [];

  readonly movementTypeOptions = [
    { label: 'Inbound / receipt', value: 'ENTRY' }, { label: 'Reservation', value: 'RESERVE' },
    { label: 'Reservation release', value: 'RELEASE' }, { label: 'Dispatch / consumption', value: 'EXIT' },
    { label: 'Transfer reserve', value: 'TRANSFER_RESERVE' }, { label: 'Transfer release', value: 'TRANSFER_RELEASE' },
    { label: 'Transfer outbound', value: 'TRANSFER_OUT' }, { label: 'Transfer inbound', value: 'TRANSFER_IN' },
    { label: 'Destruction', value: 'DESTRUCTION' }, { label: 'Refurbishment', value: 'REFURBISHMENT_MARK' },
    { label: 'Quality release', value: 'QUALITY_RELEASE' }, { label: 'Quality reject', value: 'QUALITY_REJECT' }
  ];

  searchForm = this.fb.group({
    keyword: [''], crmClientId: [null as number | null], crmAccountId: [null as number | null],
    itemId: [null as number | null], stockUnitId: [null as number | null], regionId: [null as number | null],
    warehouseId: [null as number | null], zoneId: [null as number | null], conditionId: [null as number | null],
    movementType: [null as string | null], referenceType: [null as string | null], referenceNumber: [''],
    movementFrom: [this.daysAgo(29)], movementTo: [new Date()]
  });

  ngOnInit(): void {
    this.patchFromQuery();
    this.loadReferenceData();
  }

  loadRows(event?: TableLazyLoadEvent): void {
    if (!this.referenceDataLoaded) return;
    if (event) {
      this.first = Number(event.first || 0);
      this.pageSize = Number(event.rows || this.pageSize);
    }
    this.isLoading = true;
    this.warehousingService.getItemTransactions(this.searchFilters(), Math.floor(this.first / this.pageSize), this.pageSize)
      .subscribe({
        next: response => {
          this.ledger = response.data;
          this.rows = response.data?.rows || [];
          this.totalRecords = response.data?.totalElements || 0;
          this.isLoading = false;
        },
        error: () => this.isLoading = false
      });
  }

  applySearch(): void { this.first = 0; this.loadRows(); }

  clearSearch(): void {
    const itemId = this.searchForm.value.itemId ?? null;
    const stockUnitId = this.searchForm.value.stockUnitId ?? null;
    this.searchForm.reset({
      keyword: '', crmClientId: null, crmAccountId: null, itemId, stockUnitId, regionId: null, warehouseId: null,
      zoneId: null, conditionId: null, movementType: null, referenceType: null, referenceNumber: '',
      movementFrom: this.daysAgo(29), movementTo: new Date()
    });
    this.first = 0;
    this.loadRows();
  }

  exportRows(format: 'xlsx' | 'csv'): void {
    this.isExporting = true;
    this.warehousingService.exportItemTransactions(this.searchFilters(), format).subscribe({
      next: blob => { this.downloadBlob(blob, `item-transactions-ledger.${format}`); this.isExporting = false; },
      error: () => this.isExporting = false
    });
  }

  itemOptions(): (WarehousingItem & { displayLabel: string })[] {
    return this.items.map(item => ({ ...item, displayLabel: [item.itemName, item.itemNumber, item.skuNumber, itemSizeLabel(item)].filter(Boolean).join(' | ') }));
  }

  movementLabel(row: WarehousingStockMovement): string { return [row.movementType, row.referenceType].filter(Boolean).join(' | ') || '-'; }
  routeLabel(row: WarehousingStockMovement): string { return [row.regionName, row.warehouseName, row.zoneName].filter(Boolean).join(' / ') || '-'; }
  statusFlow(row: WarehousingStockMovement): string { return [row.fromStatus, row.toStatus].filter(Boolean).join(' → ') || '-'; }
  number(value?: number | null): string { return new Intl.NumberFormat('en-US', { maximumFractionDigits: 4 }).format(value || 0); }
  signed(value?: number | null): string { return value == null ? '—' : `${value > 0 ? '+' : ''}${this.number(value)}`; }

  effectSeverity(row: WarehousingItemTransactionLedgerRow): 'success' | 'warn' | 'info' | 'secondary' | 'danger' {
    switch (row.movement.ledgerEffect) {
      case 'INBOUND': return 'success'; case 'OUTBOUND': return 'danger'; case 'RESERVE': return 'warn';
      case 'RELEASE': return 'info'; default: return 'secondary';
    }
  }

  private loadReferenceData(): void {
    this.isLoading = true;
    forkJoin({
      clients: this.crmService.getClients(), accounts: this.crmService.getAccounts(), items: this.warehousingService.getItems(),
      regions: this.warehousingService.getMasterData('REGION'), warehouses: this.warehousingService.getMasterData('WAREHOUSE'),
      zones: this.warehousingService.getMasterData('ZONE'), conditions: this.warehousingService.getMasterData('ITEM_CONDITION')
    }).subscribe({
      next: response => {
        this.clients = response.clients || []; this.accounts = response.accounts || []; this.items = response.items.data || [];
        this.masterData.REGION = response.regions.data || [];
        this.masterData.WAREHOUSE = companyOwnedWarehouses(response.warehouses.data || []);
        this.masterData.ZONE = response.zones.data || []; this.masterData.ITEM_CONDITION = response.conditions.data || [];
        this.referenceDataLoaded = true;
        this.loadRows();
      },
      error: () => this.isLoading = false
    });
  }

  private patchFromQuery(): void {
    const query = this.route.snapshot.queryParamMap;
    const itemId = Number(query.get('itemId'));
    const stockUnitId = Number(query.get('stockUnitId'));
    const movementFrom = query.get('movementFrom');
    const movementTo = query.get('movementTo');
    this.searchForm.patchValue({
      ...(Number.isFinite(itemId) && itemId > 0 ? { itemId } : {}),
      ...(Number.isFinite(stockUnitId) && stockUnitId > 0 ? { stockUnitId } : {}),
      ...(query.get('referenceType') ? { referenceType: query.get('referenceType') } : {}),
      ...(query.get('referenceNumber') ? { referenceNumber: query.get('referenceNumber') } : {}),
      ...(movementFrom ? { movementFrom: new Date(`${movementFrom}T00:00:00`) } : {}),
      ...(movementTo ? { movementTo: new Date(`${movementTo}T00:00:00`) } : {})
    });
  }

  private searchFilters(): WarehousingSearchFilters {
    const value = this.searchForm.getRawValue();
    return { ...value, keyword: value.keyword?.trim() || null, referenceType: value.referenceType?.trim() || null, referenceNumber: value.referenceNumber?.trim() || null };
  }

  private daysAgo(days: number): Date { const value = new Date(); value.setDate(value.getDate() - days); return value; }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url; anchor.download = filename; document.body.appendChild(anchor); anchor.click();
    document.body.removeChild(anchor); window.URL.revokeObjectURL(url);
  }
}
